import { SelectDataItem } from '@abg/kit-base';
import {
  ADDRESS_LINE_REGEX,
  CITY_REGEX,
  POSTCODE_ALLOW_EMPTY_REGEX,
} from '@abg/shared/regex';
import { AemPageConfig } from '@abg/typings';

import {
  ADDRESS_LINE_MAX_LENGTH,
  ADDRESS_LINE_MIN_LENGTH,
  CITY_LABELED_AS_SUBURB_COUNTRIES,
  CITY_MAX_LENGTH,
  CITY_SHOWN_BY_DEFAULT_COUNTRIES,
  POSTAL_CODE_MAX_LENGTH,
  POSTAL_CODE_MIN_LENGTH,
  STATE_SHOWN_BY_DEFAULT_COUNTRIES,
} from '../../shared/constants/registration-form.constants';
import { decodeHtmlEntities } from '../../shared/utils';

import { FieldSize } from './regions/namApac/contact-information-nam-apac.constants';
import {
  ContactInfoFieldsTranslation,
  ControlConfig,
  CountriesMap,
  UseTranslation,
} from './types';

export const getTranslations = (
  pageConfig: AemPageConfig,
  selectedCountry: string,
  t: UseTranslation,
): ContactInfoFieldsTranslation => {
  const { formSuburbSupportedCountries } = pageConfig;

  const country = {
    label: t('global.field.country.label'),
    placeholder: t('global.field.country.placeholder'),
  };

  const addressLine1 = {
    label: t('global.field.addressLine1.label'),
    placeholder: t('global.field.addressLine1.placeholder'),
  };

  const addressLine2 = {
    label: t('global.field.addressLine2.label'),
    placeholder: t('global.field.addressLine2.placeholder'),
  };

  const isSuburb = formSuburbSupportedCountries?.includes(selectedCountry);
  const addressLine3 = {
    label: isSuburb
      ? t('global.field.suburb.label')
      : t('global.field.addressLine3.label'),
    placeholder: isSuburb
      ? t('global.field.suburb.placeholder')
      : t('global.field.addressLine3.placeholder'),
  };

  const postalCode = {
    label: t('global.field.zipPostalCode.label'),
    placeholder: t('global.field.zipPostalCode.placeholder'),
  };

  const isCityLabeledAsSuburb =
    CITY_LABELED_AS_SUBURB_COUNTRIES.includes(selectedCountry);
  const city = {
    label: isCityLabeledAsSuburb
      ? t('global.field.suburb.label')
      : t('global.field.city.label'),
    placeholder: isCityLabeledAsSuburb
      ? t('global.field.suburb.placeholder')
      : t('global.field.city.placeholder'),
  };

  const state = {
    label: t('global.field.stateProvince.label'),
    placeholder: t('global.field.stateProvince.placeholder'),
  };

  return {
    country,
    addressLine1,
    addressLine2,
    addressLine3,
    postalCode,
    city,
    state,
  };
};

export const getValidationRules = (
  translation: ContactInfoFieldsTranslation,
  t: UseTranslation,
) => {
  const country = () => ({
    required: t('global.field.validation.fieldRequired.message', {
      field: translation.country.label,
    }),
  });

  const addressLine1 = () => ({
    required: t('global.field.validation.fieldRequired.message', {
      field: translation.addressLine1.label,
    }),
    minLength: {
      value: ADDRESS_LINE_MIN_LENGTH,
      message: decodeHtmlEntities(
        t('global.field.validation.invalidLength.message', {
          field: t('global.field.addressLine1.label'),
          minLength: ADDRESS_LINE_MIN_LENGTH,
          maxLength: ADDRESS_LINE_MAX_LENGTH,
        }),
      ),
    },
    maxLength: {
      value: ADDRESS_LINE_MAX_LENGTH,
      message: decodeHtmlEntities(
        t('global.field.validation.invalidLength.message', {
          field: t('global.field.addressLine1.label'),
          minLength: ADDRESS_LINE_MIN_LENGTH,
          maxLength: ADDRESS_LINE_MAX_LENGTH,
        }),
      ),
    },
    pattern: {
      value: ADDRESS_LINE_REGEX,
      message: decodeHtmlEntities(
        t('global.field.addressLine1.invalid.message'),
      ),
    },
  });

  const addressLine2 = () => ({
    minLength: {
      value: ADDRESS_LINE_MIN_LENGTH,
      message: decodeHtmlEntities(
        t('global.field.validation.invalidLength.message', {
          field: t('global.field.addressLine1.label'),
          minLength: ADDRESS_LINE_MIN_LENGTH,
          maxLength: ADDRESS_LINE_MAX_LENGTH,
        }),
      ),
    },
    maxLength: {
      value: ADDRESS_LINE_MAX_LENGTH,
      message: decodeHtmlEntities(
        t('global.field.validation.invalidLength.message', {
          field: t('global.field.addressLine2.label'),
          minLength: ADDRESS_LINE_MIN_LENGTH,
          maxLength: ADDRESS_LINE_MAX_LENGTH,
        }),
      ),
    },
    pattern: {
      value: ADDRESS_LINE_REGEX,
      message: decodeHtmlEntities(
        t('global.field.addressLine2.invalid.message'),
      ),
    },
  });

  const addressLine3 = (visible: boolean) => ({
    required: {
      value: visible,
      message: t('global.field.validation.fieldRequired.message', {
        field: translation.addressLine3.label,
      }),
    },
    minLength: {
      value: ADDRESS_LINE_MIN_LENGTH,
      message: decodeHtmlEntities(
        t('global.field.validation.invalidLength.message', {
          field: t('global.field.addressLine1.label'),
          minLength: ADDRESS_LINE_MIN_LENGTH,
          maxLength: ADDRESS_LINE_MAX_LENGTH,
        }),
      ),
    },
    maxLength: {
      value: ADDRESS_LINE_MAX_LENGTH,
      message: decodeHtmlEntities(
        t('global.field.validation.invalidLength.message', {
          field: t('global.field.addressLine3.label'),
          minLength: ADDRESS_LINE_MIN_LENGTH,
          maxLength: ADDRESS_LINE_MAX_LENGTH,
        }),
      ),
    },
    pattern: {
      value: ADDRESS_LINE_REGEX,
      message: decodeHtmlEntities(
        t('global.field.addressLine3.invalid.message'),
      ),
    },
  });

  const postalCode = (visible: boolean) => ({
    required: {
      value: visible,
      message: decodeHtmlEntities(
        t('global.field.validation.fieldRequired.message', {
          field: translation.postalCode.label,
        }),
      ),
    },
    minLength: {
      value: POSTAL_CODE_MIN_LENGTH,
      message: decodeHtmlEntities(
        t('global.field.validation.invalidLength.message', {
          field: t('global.field.zipPostalCode.label'),
          minLength: POSTAL_CODE_MIN_LENGTH,
          maxLength: POSTAL_CODE_MAX_LENGTH,
        }),
      ),
    },
    maxLength: {
      value: POSTAL_CODE_MAX_LENGTH,
      message: decodeHtmlEntities(
        t('global.field.validation.invalidLength.message', {
          field: t('global.field.zipPostalCode.label'),
          minLength: POSTAL_CODE_MIN_LENGTH,
          maxLength: POSTAL_CODE_MAX_LENGTH,
        }),
      ),
    },
    pattern: {
      value: POSTCODE_ALLOW_EMPTY_REGEX,
      message: decodeHtmlEntities(t('global.field.zipcode.invalid.message')),
    },
  });

  const city = (visible: boolean) => ({
    required: {
      value: visible,
      message: t('global.field.validation.fieldRequired.message', {
        field: translation.city.label,
      }),
    },
    maxLength: {
      value: CITY_MAX_LENGTH,
      message: decodeHtmlEntities(
        t('global.field.validation.invalidLength.message', {
          field: t('global.field.city.label'),
          maxLength: CITY_MAX_LENGTH,
        }),
      ),
    },
    pattern: {
      value: CITY_REGEX,
      message: decodeHtmlEntities(t('global.field.city.invalid.message')),
    },
  });

  const state = (visible: boolean) => ({
    required: {
      value: visible,
      message: decodeHtmlEntities(
        t('global.field.validation.fieldRequired.message', {
          field: translation.state.label,
        }),
      ),
    },
  });

  return {
    country,
    addressLine1,
    addressLine2,
    addressLine3,
    postalCode,
    city,
    state,
  };
};

export const getControlsConfig = (
  pageConfig: AemPageConfig,
  countriesData: SelectDataItem[],
  statesData: SelectDataItem[],
  selectedCountry: string,
  isPostalCodeFilled: boolean,
  t: UseTranslation,
  rootPath: string = '',
): ControlConfig[] => {
  const translations = getTranslations(pageConfig, selectedCountry, t);
  const validationRules = getValidationRules(translations, t);
  const fieldsVisibility = getFieldsVisibility(
    pageConfig,
    selectedCountry,
    isPostalCodeFilled,
  );

  const addressLine3NotRequired = ['NZ'].includes(selectedCountry)
    ? { required: false, validationRules: false }
    : { required: true, validationRules: fieldsVisibility.addressLine3 };

  return [
    {
      name: `${rootPath}country`,
      testId: `${rootPath}country`,
      visible: true,
      required: true,
      translation: translations['country'],
      validationRules: validationRules.country(),
      type: 'select',
      selectData: countriesData,
      defaultValue: pageConfig.country?.toUpperCase() ?? '',
      getSize: () => FieldSize.full,
    },
    {
      name: `${rootPath}addressLine1`,
      testId: `${rootPath}address-line-1`,
      visible: true,
      required: true,
      translation: translations['addressLine1'],
      validationRules: validationRules.addressLine1(),
      type: 'text',
      getSize: (isRegistration) =>
        isRegistration ? FieldSize.half : FieldSize.full,
    },
    {
      name: `${rootPath}addressLine2`,
      testId: `${rootPath}address-line-2`,
      visible: true,
      required: false,
      translation: translations['addressLine2'],
      validationRules: validationRules.addressLine2(),
      type: 'text',
      getSize: (isRegistration) =>
        isRegistration ? FieldSize.half : FieldSize.full,
    },
    {
      name: `${rootPath}addressLine3`,
      testId: `${rootPath}address-line-3`,
      visible: fieldsVisibility.addressLine3,
      required: addressLine3NotRequired.required,
      translation: translations['addressLine3'],
      validationRules: validationRules.addressLine3(
        addressLine3NotRequired.validationRules,
      ),
      type: 'text',
      getSize: (isRegistration) =>
        isRegistration ? FieldSize.half : FieldSize.full,
    },
    {
      name: `${rootPath}postalCode`,
      testId: `${rootPath}postal-code`,
      visible: fieldsVisibility.postalCode,
      required: true,
      translation: translations['postalCode'],
      validationRules: validationRules.postalCode(fieldsVisibility.postalCode),
      type: 'text',
      maxLength: POSTAL_CODE_MAX_LENGTH,
      getSize: (isRegistration) =>
        isRegistration ? FieldSize.half : FieldSize.full,
    },
    {
      name: `${rootPath}city`,
      testId: `${rootPath}city`,
      visible: fieldsVisibility.city,
      required: true,
      translation: translations['city'],
      maxLength: CITY_MAX_LENGTH,
      validationRules: validationRules.city(fieldsVisibility.city),
      type: 'text',
      getSize: (isRegistration) =>
        isRegistration ? FieldSize.half : FieldSize.full,
    },
    {
      name: `${rootPath}state`,
      testId: `${rootPath}state`,
      visible: fieldsVisibility.state,
      required: true,
      translation: translations['state'],
      validationRules: validationRules.state(true),
      type: 'select',
      selectData: statesData,
      getSize: (isRegistration) =>
        isRegistration ? FieldSize.half : FieldSize.full,
    },
  ];
};

/**
 * Builds the supported countries list based on the AEM page configuration
 * and the country data from /content/admin/countrylist.json.
 *
 * @param pageConfig - AEM page configuration containing the supported countries list
 * @param countriesMap - Map of country codes to country metadata
 * @returns Ordered array of SelectDataItem objects, preserving the order defined in the AEM configuration
 */
export const getCountriesData = (
  { supportedCountries = [] }: AemPageConfig,
  countriesMap: CountriesMap = {},
): SelectDataItem[] => {
  return supportedCountries.map(
    (country): SelectDataItem => ({
      value: country,
      label: countriesMap[country]?.name ?? country,
    }),
  );
};

export const getStatesData = (
  countriesMap: CountriesMap,
  selectedCountry: string,
): SelectDataItem[] => {
  return (
    countriesMap[selectedCountry]?.stateList?.map((state) => ({
      label: state.stateName,
      value: state.stateCode,
    })) || []
  );
};

const getFieldsVisibility = (
  pageConfig: AemPageConfig,
  selectedCountry: string,
  isPostalCodeFilled: boolean,
) => {
  const {
    formAddressLineHiddenCountries,
    formCitySupportedCountries,
    formStateSupportedCountries,
    formZipSupportedCountries,
  } = pageConfig;

  return {
    addressLine3: !formAddressLineHiddenCountries?.includes(selectedCountry),
    postalCode: formZipSupportedCountries?.includes(selectedCountry),
    city: isDependentFieldVisible(
      formCitySupportedCountries,
      CITY_SHOWN_BY_DEFAULT_COUNTRIES,
      selectedCountry,
      isPostalCodeFilled,
    ),
    state: isDependentFieldVisible(
      formStateSupportedCountries,
      STATE_SHOWN_BY_DEFAULT_COUNTRIES,
      selectedCountry,
      isPostalCodeFilled,
    ),
  };
};

const isDependentFieldVisible = (
  supportedCountries: string[],
  skipDependencyCountries: string[],
  selectedCountry: string,
  isDependencyMet: boolean,
) => {
  if (!supportedCountries?.includes(selectedCountry)) return false;

  return skipDependencyCountries.includes(selectedCountry)
    ? true
    : isDependencyMet;
};
